
import { RichSelectModule } from "ag-grid-enterprise";
import React, { useState, useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ModuleRegistry, CellClassParams } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css"
import { DocMetaData } from "../utils/docMetaData";
import { useEntityContext } from "../context/EntityContext";
// import { Input, Select, SelectItem, Spinner } from "@heroui/react";
import { getYearsRange, getQuarterOptions } from "../utils/fileUploadGrid";
import { format } from "date-fns";
import axiosInstance from "../utils/axiosSetup";
import { toast } from "react-toastify";
import { previewFile } from "../utils/previewFile";
import { DropdownArrowMarker } from "./DropdownArrowMarker";
import { parseDateUtility } from "../utils/normalizeDocMetaData";
ModuleRegistry.registerModules([RichSelectModule])


const FileUploadGrid: React.FC<{
    rowData: DocMetaData[];
    setRowData: React.Dispatch<React.SetStateAction<DocMetaData[]>>;
    onUpload?: () => void;
    onFilePreviewClick?:(fileName: string, fileUrl: string) => void;
}> = ({
    setRowData,
    rowData,
    onFilePreviewClick
}) => {

        const { entityMap } = useEntityContext();

        const documentTypeOptions = [
            "IC Memo",
            "AGM",
            "Quarterly Letters",
            // "Quarterly Updates",
            "Quarterly Notes",
            "Finance Input",
            "TermSheets",
        ]

        const isExcel = (fileName?: string) =>
            typeof fileName === 'string' && /\.(xlsx|xls)$/i.test(fileName);
        const isFinanceExcelRow = (data: any) =>
            isExcel(data.fileName) && data.documentType === 'Finance Input';

        // Handler to delete a row by fileName

        const handleDeleteRow = useCallback((fileName: string) => {
            setRowData((current) => current.filter(row => row.fileName !== fileName));
        }, [setRowData]);

        //Returns true if the cell value is empty or whitespace

        const isEmptyCell = (value: any) => {
            if(value === null || value === undefined) return true;

            if(typeof value === "string" && value.trim() === "") return true;
            return false;
        };

        //Cell class rules for empty cells: adds 'empty-cell' class

        const emptyCellClassRule = (params: CellClassParams) => {
            const excelCells = isExcel(params.data?.fileName);
            if(excelCells) return false;
            return isEmptyCell(params.value);
        };


        const handleCellValueChange = useCallback((params: any) => {
            if (!params?.data) return;

            const updatedRow = { ...params.data };
    
            //Reset dependent fields if parent changes
            if (params.colDef.field === "documentType") {
                updatedRow.documentDate = "";

            }

            if (params.colDef.field === "entityType") {
                updatedRow.entity = "";

            }



            //Update rowData
            const updatedData = rowData.map((row) =>
                row.fileName === updatedRow.fileName ? updatedRow : row
            );
            setRowData(updatedData)
            console.log('rowData', rowData)


        }, [rowData, setRowData]);

        



        const columnDefs = useMemo<ColDef[]>(() => [

            //Entity Type column 
            {
                headerName: "Type",
                field: "entityType",
                flex:1,
                editable: (params: any) => {
                    return !isExcel(params.data?.fileName);

                },
                cellEditor: "agSelectCellEditor",
                cellEditorParams: {
                    values: Object.keys(entityMap),
                },
              
                cellClassRules: {
                    "empty-cell": emptyCellClassRule,
                },

                cellRenderer: (params: any) => {
                    const editable = typeof params.colDef.editable === "function"
                        ? params.colDef.editable(params)
                        : !!params.colDef.editable;
                    
                        return <DropdownArrowMarker value={params.value} isEditable={editable}  />;
                },


            },
            //Entity column 
            {
                headerName: "Entity",
                field: "entity",
                minWidth: 220,
                flex: 1.5,
                tooltipField:"entity",
                editable: (params: any) => {
                    return !isExcel(params.data?.fileName);

                },
                cellEditor: 'agRichSelectCellEditor',
                cellEditorParams: (params: any) => {
                    const entityType = params.data?.entityType;
                    const options = entityMap[entityType]?.map(e => e?.name).filter(name => typeof name === "string" && name.trim() !== "") || [];
                    return {
                        values: options,
                        allowTyping: true,
                        filterList: true,
                        highlightMatch: true,
                        searchType: 'matchAny',
                        valueListMaxHeight: 200,
                        cellEditorPopup: true,
                    }
                },
                cellClassRules: {
                    "empty-cell": emptyCellClassRule,
                },
                cellRenderer: (params: any) => {
                    const editable = typeof params.colDef.editable === "function"
                        ? params.colDef.editable(params)
                        : !!params.colDef.editable;
                    
                        return <DropdownArrowMarker value={params.value} isEditable={editable}  />;
                },

            },

            //File Name column 
            {
                headerName: "File Name",
                field: "fileName",
                editable: false,
                minWidth: 320,
                flex:2,
                tooltipField: "fileName",
                cellRenderer: (params: any) => {
                    const fileName = params.value;
                    const downloadUrl = params.data?.fileDownloadUrl;
                    // console.log('download url is ', downloadUrl)

                    if(!fileName || !downloadUrl) return fileName || "-";

                    const decodeUrl = decodeURIComponent(downloadUrl);

                    // console.log("decodedURL is", decodeUrl);

                    //Construct final backend url 
                    const backendUrl = `/uploader/download_doc?path=${decodeUrl}`;

                    return (
                        <span 
                            onClick={() => onFilePreviewClick?.(fileName, backendUrl)}
                                style={{
                                    color: "#0d6efd", 
                                    textDecoration: "underline", 
                                    cursor: "pointer",
                                }}
                            >
                                    
                                {fileName}
                        </span>
                    );
                }
            },

            //Document Type Column
            {
                headerName: "Document Type",
                field: "documentType",
                minWidth: 160,
                flex:1.5,
                editable: (params: any) => {
                    const fileName = params.data?.fileName;
                    return !isExcel(fileName); // only allow editable if excel
                },
                cellEditor: "agSelectCellEditor",
                cellEditorParams: (params: any): { values: string[] } => {
                    const fileName = params.data?.fileName;
                    return {
                        values: isExcel(fileName)
                            ? ["Finance Input"]
                            : documentTypeOptions.filter(d => d !== "Finance Input"),
                    };
                },
               
                cellClassRules: {
                    "empty-cell": emptyCellClassRule,
                },

                cellRenderer: (params: any) => {
                    const editable = typeof params.colDef.editable === "function"
                        ? params.colDef.editable(params)
                        : !!params.colDef.editable;
                    
                        return <DropdownArrowMarker value={params.value} isEditable={editable}  />;
                },


            },
            //Author Date Column
            // {
            //     headerName: "Author Date",
            //     field: "authorDate",
            //     flex:1,
            //     minWidth:140,
            //     editable: (params: any) => {
            //         return !isExcel(params.data.fileName);

            //     },
            //     cellEditor: 'agDateCellEditor',
               
            //     cellClassRules: {
            //         "empty-cell": emptyCellClassRule,
            //     },
            //     valueGetter: (params: any) => {
            //         const val = params.data?.authorDate;
            //         if(!val) return "";
            //         return format(new Date(val), "yyyy-MM-dd");
            //     },
            //     valueSetter:(params: any) => {
            //         const parsed = parseDateUtility(params.newValue);
            //         if(!parsed) return false;
            //         params.data.authorDate = format(parsed, "yyyy-MM-dd");
            //         return true;
            //     },
            //     cellRenderer: (params: any) => {
            //         const editable = typeof params.colDef.editable === "function"
            //             ? params.colDef.editable(params)
            //             : !!params.colDef.editable;
                    
            //             return <DropdownArrowMarker value={params.value} isEditable={editable} forceDateFormat />;
            //     },

            // },

            // Document date column ( Need implemenattaion and fixes )
            {
                headerName: "Document Date",
                field: "documentDate",
                flex:1,
                minWidth:140,
                editable: (params: any) => {
                    return !isExcel(params.data.fileName);

                },
                cellEditorSelector: (params: any) => {
                    const type = params.data.documentType;
                    if (type === "AGM") {
                        return {
                            component: "agSelectCellEditor",
                            params: {
                                values: getYearsRange().map(String)
                            }
                        };

                    } else if (
                        type === "Quarterly Letters" ||
                        // type === "Quarterly Updates" ||
                        type === "Quarterly Notes"
                    ) {
                        return {
                            component: 'agRichSelectCellEditor',
                            params: {
                                values: getQuarterOptions(),
                                allowTyping: true,
                                searchType: 'matchAny',
                                filterList: true,
                                highlightMatch: true,
                                valueListMaxHeight: 220,
                                cellEditorPopup: true,
                            }
                        };
                    } else if (type === "IC Memo"  || type === "TermSheets") {
                        return {
                            component: "agDateCellEditor",

                        }

                    }

                    return undefined;
                },

                valueGetter: (params: any) => {
                    const val = params.data?.documentDate;
                    if(!val) return "";

                    if(typeof val === "string" && /^\d{4}_\d{2}_\d{2}$/.test(val)) {
                        return val.replace(/_/g,"-");
                    }
                    return val;


                },

                valueSetter: (params: any) => {
                    const { newValue, data} = params;
                    const docType = data.documentType;

                    if( docType === "AGM") {
                        if(/^\d{4}$/.test(newValue)) {
                            data.documentDate = newValue;
                            return true;
                        }
                    } else if (docType === "IC Memo" || docType === "TermSheets") {
                        const parsed = parseDateUtility(newValue);
                        if(!parsed) return false;
                        data.documentDate =  format(parsed, "yyyy-MM-dd");
                        return true;
                    } else {
                        data.documentDate = newValue;
                        return true;
                    }
                    return false;
                },
                
                cellClassRules: {
                    "empty-cell": emptyCellClassRule,
                },
                cellRenderer: (params: any) => {
                    const editable = typeof params.colDef.editable === "function"
                        ? params.colDef.editable(params)
                        : !!params.colDef.editable;

                    const docType = params.data?.documentType;
                    
                        return <DropdownArrowMarker value={params.value} isEditable={editable} forceDateFormat={docType === "IC Memo" || docType === "TermSheets"} />;
                },
         
            },

            // New Delete Column
            {
                headerName: "",
                field: "delete",
                editable: false,
                filter: false,
                sortable:false,
                width: 60,
                pinned: 'right',
                cellRenderer: (params: any) => {
                    return (
                        <button 
                            aria-label={`Delete row for file ${params.data.fileName}`}
                            title="Delete row"
                            className="delete-btn"
                            onClick={() => handleDeleteRow(params.data.fileName)}
                        >
                            <img src="/images/trashBin.svg" alt="DeleteRow" />
                        </button>
                    );
                }
            }
        ], [entityMap, handleDeleteRow]);




        const defaultColDef = useMemo<ColDef>(() => ({
            resizable: true,
            sortable: true,
            autoHeight: true,
            // flex: 1
        }), [])



        return (
            <div data-testid="file-upload-grid" className="ag-theme-alpine mt-6 w-full h-[600px]" >
                <AgGridReact
                    rowData={rowData}
                    getRowId={(params) => params.data.fileName}
                    columnDefs={columnDefs}
                    onCellValueChanged={handleCellValueChange}
                    domLayout="autoHeight"
                    defaultColDef={defaultColDef}
                    stopEditingWhenCellsLoseFocus={true}
                    singleClickEdit={true}
                    pagination={true}
                    paginationPageSize={500}
                    paginationPageSizeSelector={[20,50,100,500]}

                />
            </div>
        );

    };

export default FileUploadGrid;

